import { ACTIONS } from './constants';
import { createAPIClient, API_ERROR_TYPES } from 'api/client';
import { CHALLENGE_STATUS } from './constants';
import { initialState } from './initialState';

const handleAPIError = (error) => {
  console.error('API Error:', {
    type: error.type,
    message: error.message,
    status: error.status,
    originalError: error.originalError
  });
  
  switch (error.type) {
    case API_ERROR_TYPES.UNAUTHORIZED:
      return 'Your session has expired. Please log in again.';
    case API_ERROR_TYPES.FORBIDDEN:
      return 'You don\'t have permission to perform this action.';
    case API_ERROR_TYPES.NOT_FOUND:
      return 'The requested resource was not found.';
    case API_ERROR_TYPES.VALIDATION:
      return error.message || 'Please check your input and try again.';
    case API_ERROR_TYPES.NETWORK_ERROR:
      return 'Please check your internet connection.';
    default:
      return 'An unexpected error occurred. Please try again.';
  }
};

export const handlers = (dispatch, teamId) => {
  // Function to get current state from the store
  const getState = () => {
    // We'll use a callback to get the latest state when needed
    let currentState;
    dispatch((state) => {
      currentState = state;
      return state; // Return unchanged state
    });
    return currentState;
  };

  // Keep a reference to the initialized API client
  let apiClientPromise = null;

  const getAPIClient = async () => {
    if (!apiClientPromise) {
      apiClientPromise = createAPIClient();
    }
    return apiClientPromise;
  };

  const createHandlerWithAPI = (apiCall, successAction) => async (...args) => {
    if (!teamId) {
      console.error('No team ID available');
      return { 
        data: null, 
        error: 'No team ID available. Please ensure you are part of a team.' 
      };
    }

    try {
      const api = await getAPIClient();
      const data = await apiCall(api, ...args);
      
      if (successAction) {
        const action = successAction(data);
        dispatch(action);
      }
      
      return { data, error: null };
    } catch (error) {
      return { 
        data: null, 
        error: handleAPIError(error)
      };
    }
  };

  return {
    // Core challenge operations
    fetchChallenges: createHandlerWithAPI(
      (api) => api.challenges.fetch(),
      (data) => {
        const state = getState();
        // console.warn('Current state in fetchChallenges:', {
        //   hasState: !!state,
        //   hasChallenges: !!state?.challenges,
        //   hasCategories: !!state?.challenges?.categories
        // });
        
        // Ensure we have the required state structure
        const categories = state?.challenges?.categories || initialState.challenges.categories;
        
        return {
          type: ACTIONS.SET_CHALLENGES,
          payload: {
            instances: data.reduce((acc, challenge) => {
              acc[challenge.id] = challenge;
              return acc;
            }, {}),
            categories: Object.keys(categories).reduce((acc, categoryId) => {
              // Preserve existing category structure while updating instanceIds
              acc[categoryId] = {
                ...categories[categoryId], // Keep existing category data
                instanceIds: data
                  .filter(c => {
                    if (categoryId === 'uncategorized') {
                      return !c.category_id || c.category_id === 'uncategorized';
                    }
                    return c.category_id === categoryId;
                  })
                  .map(c => c.id)
              };
              return acc;
            }, {})
          }
        };
      }
    ),

    addChallengeInstance: createHandlerWithAPI(
      (api, instance) => api.challenges.create({
        ...instance,
        team_id: teamId,
        category_id: instance.category_id || 'uncategorized'
      }),
      (data) => ({
        type: ACTIONS.ADD_CHALLENGE_INSTANCE,
        payload: {
          instance: data,
          category_id: data.category_id || 'uncategorized'
        }
      })
    ),

    updateChallengeInstance: createHandlerWithAPI(
      (api, challengeId, updates) => api.challenges.update(challengeId, {
        ...updates,
        team_id: teamId
      }),
      (data) => ({
        type: ACTIONS.UPDATE_CHALLENGE_INSTANCE,
        payload: {
          instance: data,
          category_id: data.category_id
        }
      })
    ),

    // Category operations - these now directly use updateChallengeInstance
    attachCategory: async (category_id, instance_id) => {
      const state = getState();
      const instance = state.challenges.instances[instance_id];
      if (!instance) return { error: 'Instance not found' };

      return handlers(dispatch, teamId).updateChallengeInstance(instance_id, {
        ...instance,
        category_id
      });
    },

    // Archive operation
    archiveChallengeInstance: async (challengeId) => {
      return handlers(dispatch, teamId).updateChallengeInstance(challengeId, {
        is_archived: true
      });
    },

    // Local state handlers with API persistence
    detachCategory: (category_id, instance_id) => {
      dispatch({
        type: ACTIONS.DETACH_CATEGORY,
        payload: { category_id, instance_id }
      });
    },

    attachChallenge: async (category_id, challenge) => {
      // Update instance to ACTIVE status
      return handlers(dispatch, teamId).updateChallengeInstance(challenge.id, {
        ...challenge,
        status: CHALLENGE_STATUS.ACTIVE,
        category_id
      });
    },

    detachChallenge: async (category_id, challenge_id) => {
      // Update instance back to DRAFT status
      const state = getState();
      const instance = state.challenges.instances[challenge_id];
      if (!instance) return { error: 'Instance not found' };

      return handlers(dispatch, teamId).updateChallengeInstance(challenge_id, {
        ...instance,
        status: CHALLENGE_STATUS.DRAFT
      });
    },

    // Draft-related handlers
    addChallengeDraft: createHandlerWithAPI(
      (api, category_id, draft) => {
        const draftId = draft.id || `draft-${Date.now()}`;
        return api.challenges.create({
          ...draft,
          id: draftId,
          category_id,
          team_id: teamId,
          status: CHALLENGE_STATUS.DRAFT,
          data: {
            opportunities: [],
            risks: [],
            active_item_type: 'opportunities'
          }
        });
      },
      (data) => ({
        type: ACTIONS.ADD_CHALLENGE_INSTANCE,
        payload: {
          instance: data,
          category_id: data.category_id
        }
      })
    ),

    deleteDraft: createHandlerWithAPI(
      (api, category_id, draft_id) => api.challenges.delete(draft_id),
      (data) => ({
        type: ACTIONS.DELETE_DRAFT,
        payload: { category_id, draft_id }
      })
    ),

    addDraftOpportunity: (category_id, draft_id, content) => {
      dispatch({
        type: ACTIONS.ADD_DRAFT_OPPORTUNITY,
        payload: { category_id, draft_id, content }
      });
    },

    addDraftRisk: (category_id, draft_id, content) => {
      dispatch({
        type: ACTIONS.ADD_DRAFT_RISK,
        payload: { category_id, draft_id, content }
      });
    },

    updateDraftOpportunity: (category_id, draft_id, opportunity_id, content) => {
      dispatch({
        type: ACTIONS.UPDATE_DRAFT_OPPORTUNITY,
        payload: { category_id, draft_id, opportunity_id, content }
      });
    },

    updateDraftRisk: (category_id, draft_id, risk_id, content) => {
      dispatch({
        type: ACTIONS.UPDATE_DRAFT_RISK,
        payload: { category_id, draft_id, risk_id, content }
      });
    },

    deleteDraftOpportunity: (category_id, draft_id, opportunity_id) => {
      dispatch({
        type: ACTIONS.DELETE_DRAFT_OPPORTUNITY,
        payload: { category_id, draft_id, opportunity_id }
      });
    },

    deleteDraftRisk: (category_id, draft_id, risk_id) => {
      dispatch({
        type: ACTIONS.DELETE_DRAFT_RISK,
        payload: { category_id, draft_id, risk_id }
      });
    },

    activateDraftOpportunity: (category_id, draft_id, opportunity_id) => {
      dispatch({
        type: ACTIONS.ACTIVATE_DRAFT_OPPORTUNITY,
        payload: { category_id, draft_id, opportunity_id }
      });
    },

    activateDraftRisk: (category_id, draft_id, risk_id) => {
      dispatch({
        type: ACTIONS.ACTIVATE_DRAFT_RISK,
        payload: { category_id, draft_id, risk_id }
      });
    },

    // Action Management
    addDraftAction: (category_id, draft_id, item_type, item_id, action) => {
      dispatch({
        type: ACTIONS.ADD_DRAFT_ACTION,
        payload: {
          category_id,
          draft_id,
          item_type,
          item_id,
          action
        }
      });
    },

    updateDraftAction: (category_id, draft_id, item_type, item_id, action_id, updates) => {
      dispatch({
        type: ACTIONS.UPDATE_DRAFT_ACTION,
        payload: {
          category_id,
          draft_id,
          item_type,
          item_id,
          action_id,
          updates
        }
      });
    },

    deleteDraftAction: (category_id, draft_id, item_type, item_id, action_id) => {
      dispatch({
        type: ACTIONS.DELETE_DRAFT_ACTION,
        payload: {
          category_id,
          draft_id,
          item_type,
          item_id,
          action_id
        }
      });
    },

    setActiveItemType: (category_id, draft_id, item_type) => {
      dispatch({
        type: ACTIONS.SET_ACTIVE_ITEM_TYPE,
        payload: { category_id, draft_id, item_type }
      });
    }
  };
};
